import React, { useEffect, useState } from 'react'
import Page from 'containers/Page'
import useSyntheticVoice from 'hooks/useSyntheticVoice'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { TextArea, Button, Input } from '@arnsbogroup/react-voicearchive'
import Waveform from 'components/Waveform'
import DemoPlayButton from 'containers/DemoPlayButton'
import Icon from 'components/Icon'
import classNames from 'classnames'
import useValidate, { is } from '@arnsbogroup/react-voicearchive/hooks/useValidate'
import useApi from 'hooks/useApi'
import { subscribedVoices } from 'data/voices/subscribed-voices'
import useOpenAi from 'hooks/useOpenAi'

const EmbeddedSyntheticVoices = () => {

  const { formatMessage, locale } = useIntl()

  const [leadCrmData, captureLeadIsLoading, errors, refetchLead, meta] = useApi(
    '/rest/v2/crm/capture-synthetic-voice-lead',
    { load: false }
  );

  const [syntheticVoiceState, scriptToVoice] = useSyntheticVoice();
  const [openAiState, voiceSuggest, moderateContent, suggestVoiceOverScript] = useOpenAi()

  const [history, setHistory] = useState([]);

  const [voice, setVoice] = useState(Object.keys(subscribedVoices)[0]);

  const [lead, setLead] = useState({
    leadId: null,
    name: null,
    email: null
  });

  const [form, setForm] = useState({
    script: '',
  });

  const [validations, validate] = useValidate(lead, {
    name: [is.present],
    email: [is.present, is.email]
  });

  const i18n = (messages) => messages?.map((message) => ({
    present: `Must be filled out`,
    email: `E-mail must be a valid e-mail like 'name@company.org'`,
    set: `Must be filled out`,
  }[message] || message));

  async function generateVoice (syntheticVoice) {
    if (leadCrmData && leadCrmData.leadId !== null) {
      const url = await scriptToVoice(subscribedVoices[voice].syntheticVoiceId, form.script);
      setHistory((prev) => [{ script: form.script, url, description: voice }, ...prev]);
      setForm({ ...form, script: '' });
    }
  }

  useEffect(() => {
    if (leadCrmData && leadCrmData.leadId !== null) {
      setLead({ leadId: leadCrmData.leadId, name: null, email: null });
    }
  }, [leadCrmData]);

  function createLead() {
    refetchLead('/rest/v2/crm/capture-synthetic-voice-lead', {
      method: 'POST',
      body: JSON.stringify({
        name: lead.name,
        email: lead.email
      }),
    });
  }

  async function suggestScript() {
    if (openAiState.isSuggesting == false) {
      const script = await suggestVoiceOverScript();
      setForm({ ...form, script });
    }
  }

  return (
    <Page
      childrenProps={{ className: '' }}
      nav={false}
      footer={false}
    >
      <main className="p-0 m-0 flex">
        <div className='hidden md:inline md:w-1/3 mr-8 space-y-4'>
          { Object.keys(subscribedVoices).map((subscribedVoice, index) => (
            <div key={'syn_' + index} className={classNames('p-4 border rounded', {
              'border-primary': voice === subscribedVoice,
              'border-gray-200': voice !== subscribedVoice,
            })}>
              <h3 className="block text-base text-gray-700">
                {subscribedVoice}
              </h3>

              <div className="flex">
                <Waveform src={subscribedVoices[subscribedVoice].syntheticDemoUrl} />

                <div className="flex space-x-1">
                  <DemoPlayButton
                    childClassName=""
                    demo={{ url: subscribedVoices[subscribedVoice].syntheticDemoUrl }}
                  />
                  { (voice !== subscribedVoice) ? (
                  <Button
                    variant="plain"
                    className="inline-block"
                    size="sm"
                    onClick={() =>
                      setVoice(subscribedVoice)
                    }
                  >
                    <Icon icon="plus" /> <span className="hidden sm:inline"><FormattedMessage {...messages.select} /></span>
                  </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className='w-full md:w-2/3'>
          { (lead.leadId === null) ? (
            <div className="mb-4 md:mb-16 p-8 bg-gray-100 space-y-4 rounded">
              <h1 className='font-medium text-3xl'>Try our synthetic voices</h1>
              <Input
                autoFocus
                label="Name"
                placeholder="Your full name"
                value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                onBlur={() => validate(['name'])}
                errors={i18n(validations.name)}
              />
              <Input
                label="E-mail"
                type="email"
                placeholder="E-mail address"
                value={lead.email}
                onChange={(e) => setLead({ ...lead, email: e.target.value })}
                onBlur={() => validate(['email'])}
                errors={i18n(validations.email)}
              />

              <div className="flex justify-end mt-4">
                <Button
                  size="sm"
                  onClick={() => createLead()}
                  disabled={captureLeadIsLoading}
                >
                  { captureLeadIsLoading ?
                    <span><FormattedMessage {...messages.unlocking} /></span>
                  :
                    <span><FormattedMessage {...messages.unlock} /></span>
                  }
                </Button>
              </div>
            </div>
          ) : null }


          <div className='md:hidden m-4 space-y-4'>
            { Object.keys(subscribedVoices).map((subscribedVoice, index) => (
              <div className={classNames('p-2 flex space-x-2 border rounded', {
                'border-primary': voice === subscribedVoice,
                'border-gray-200': voice !== subscribedVoice,
              })} onClick={() =>
                setVoice(subscribedVoice)
              }>
                <h3 className="block text-small text-gray-700">
                  {subscribedVoice}
                </h3>
              </div>
            ))}
          </div>

          <h2 className="text-xl font-medium mb-4">
            {voice}
          </h2>

          <TextArea
            className="text-sm"
            placeholder={messages.scriptPlaceholder.defaultMessage}
            rows={8}
            maxLength={512}
            value={form.script}
            disabled={syntheticVoiceState.isLoading || lead.leadId === null}
            onChange={(e) => {
              setForm({ ...form, script: e.target.value });
            }}
          />

          {(!syntheticVoiceState.isLoading && lead.leadId !== null) ? (
          <span className='text-sm text-positive cursor-pointer' onClick={() => suggestScript()}>
            { openAiState.isSuggesting ? formatMessage(messages.suggestionLoading) : formatMessage(messages.suggestionButton) }
          </span>
          ) : null}

          <div className="flex justify-end mt-4">
            <Button
              size="sm"
              onClick={() => generateVoice(voice)}
              disabled={syntheticVoiceState.isLoading || lead.leadId === null}
            >
              { syntheticVoiceState.isLoading ?
                <span><FormattedMessage {...messages.processing} /></span>
              :
                <span><FormattedMessage {...messages.generateVoice} /></span>
              }
            </Button>
          </div>

          <div className="mt-4">
            {history.map((item, index) => (
              <div className="mt-4 border-b border-gray-300" key={index}>
                <h3 className="font-bold">{item.description}</h3>
                <p>{item.script}</p>
                <div className="flex">
                  <Waveform src={item.url} />
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <DemoPlayButton
                      childClassName=""
                      demo={{ url: item.url }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </Page>
  )
}

export default EmbeddedSyntheticVoices
